import { Box, Grid, Typography } from '@mui/material';

import { InfoField, LinkChip, NO_DATA_PLACEHOLDER } from '@occc-app/design-system';
import { separateEveryThousandByComma } from '@occc-app/lib/numbers';
import { pluralize } from '@occc-app/lib/strings';
import { Random_Award_Type } from '@occc/graphql';
import {
  segmentIconSelectItems,
  segmentTypeSelectItems,
} from '@occc-app/routes/Game/GameConfigureForm/constants';
import { SegmentFormValues, SegmentIcon } from '@occc-app/routes/Game/types';
import { ReactNode } from 'react';
import { routeCreator } from '@occc-app/routes/AwardCatalog/utils';
import { ColorSwatch } from '@occc/ui-design-system';

const segmentTypeLabels = Object.fromEntries(
  segmentTypeSelectItems.map((item) => [item.value, item.label]),
) as Record<Random_Award_Type, string>;

const segmentIcons = Object.fromEntries(
  segmentIconSelectItems.map((item) => [item.value, { label: item.label, icon: item.icon }]),
) as Record<SegmentIcon, { label: string; icon: ReactNode }>;

const getSegmentAwardLabel = (segment: SegmentFormValues): ReactNode => {
  if (segment.type === Random_Award_Type.Points) {
    return segment.awardPoints
      ? `${segment.awardPoints} ${pluralize(segment.awardPoints, 'Point')}`
      : NO_DATA_PLACEHOLDER;
  }
  if (segment.type === Random_Award_Type.Vouchers && segment.awardOffer) {
    return (
      <LinkChip
        label={segment.awardOffer.name}
        href={routeCreator.details(segment.awardOffer.id)}
        tooltip=''
      />
    );
  }
  return NO_DATA_PLACEHOLDER;
};

type GameSegmentProps = {
  index: number;
  segment: SegmentFormValues;
};

export const GameSegment = ({ segment, index }: GameSegmentProps) => {
  const awardLabel = getSegmentAwardLabel(segment);

  return (
    <>
      <Typography variant='subtitle1' gutterBottom>
        Segment {index + 1}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <InfoField label='Segment Type' value={segmentTypeLabels[segment.type] ?? segment.type} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <InfoField
            label='Award'
            value={awardLabel}
            disableTypography={typeof awardLabel !== 'string'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <InfoField label='Award Limits' value={separateEveryThousandByComma(segment.limit)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <InfoField
            label='Segment Color'
            value={segment.color ? <ColorSwatch color={segment.color} /> : undefined}
            disableTypography
          />
        </Grid>
        {segment.type === Random_Award_Type.Vouchers && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <InfoField label='Voucher Expiration Days' value={segment.awardOfferExpirationDays} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoField
                label='Voucher Count'
                value={
                  segment.awardOfferVoucherCount
                    ? separateEveryThousandByComma(segment.awardOfferVoucherCount)
                    : NO_DATA_PLACEHOLDER
                }
              />
            </Grid>
          </>
        )}
        {!!segment.icon && (
          <Grid item xs={12} sm={6} md={3}>
            <InfoField
              label='Segment Icon'
              disableTypography={!!segmentIcons[segment.icon]}
              value={
                segmentIcons[segment.icon] ? (
                  <Box display='flex' alignItems='center' gap={1}>
                    {segmentIcons[segment.icon].icon}
                    {segmentIcons[segment.icon].label}
                  </Box>
                ) : (
                  segment.icon
                )
              }
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};
